import React, { useState, useEffect } from 'react';
import { formDataInstance } from '@/common/axios';
import CONFIG from '@/config';
import { getRestHeaders } from '@/common/utils/api';
import { capitalize } from '@mui/material/utils';
import { Box, Table, TableBody, TableRow, Typography } from '@mui/material';
import CardLoader from '@/components/CardLoader';
import printPrettyPhoneNumber from '@/common/utils/printPrettyPhoneNumber';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { AMDLinkStyles, AMDLinkTableCell, AMDLinkCard } from './index';
import { displayGender } from '@/common/utils/utils';
import { convertToStoreLocalTime } from '@/common/utils/time';

const POWClientDetails = (props: any) => {
  const { customerData } = props;
  const { enqueueSnackbar } = useSnackbar();
  const classes = AMDLinkStyles();

  const [loading, setLoading] = useState(false);
  const [POWEncounters, setPOWEncounters] = useState([]);

  const getPOWEncounters = async () => {
    try {
      setLoading(true);
      const response = await formDataInstance.get(
        CONFIG.MEDICAL_API_URL + '/staff/pow-medical-encounters/',
        {
          params: { customerId: customerData.id },
          headers: getRestHeaders()
        }
      );
      setPOWEncounters(response?.data?.data);
    } catch (error) {
      enqueueSnackbar('Error fetching POW encounters', {
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPOWEncounters();
  }, []);

  return (
    <Box pt={2} pr={2} pl={2}>
      <Box style={{ height: '255px' }}>
        <Typography className={classes.columnHeader}>
          POW client details
        </Typography>
        <Typography className={classes.powNameText} sx={{ mb: 2 }}>
          {customerData?.firstName} {customerData?.lastName}
        </Typography>
        <Table sx={{ mb: 3 }}>
          <TableBody>
            <TableRow>
              <AMDLinkTableCell align="left">
                <Typography className={classes.strongText}>
                  Date of birth
                </Typography>
              </AMDLinkTableCell>
              <AMDLinkTableCell align="right">
                <Typography className={classes.text}>
                  {moment.utc(customerData?.dob).format('MM/DD/YYYY')}
                </Typography>
              </AMDLinkTableCell>
            </TableRow>
            <TableRow>
              <AMDLinkTableCell align="left">
                <Typography className={classes.strongText}>Sex</Typography>
              </AMDLinkTableCell>
              <AMDLinkTableCell align="right">
                <Typography className={classes.text}>
                  {displayGender(customerData?.gender)}
                </Typography>
              </AMDLinkTableCell>
            </TableRow>
            <TableRow>
              <AMDLinkTableCell align="left">
                <Typography className={classes.strongText}>Phone</Typography>
              </AMDLinkTableCell>
              <AMDLinkTableCell align="right">
                <Typography className={classes.text}>
                  {printPrettyPhoneNumber(customerData?.mobilePhone)}
                </Typography>
              </AMDLinkTableCell>
            </TableRow>
            <TableRow>
              <AMDLinkTableCell align="left">
                <Typography className={classes.strongText}>Email</Typography>
              </AMDLinkTableCell>
              <AMDLinkTableCell align="right">
                <Typography className={classes.text}>
                  {customerData?.email}
                </Typography>
              </AMDLinkTableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Typography className={classes.strongText} sx={{ mb: 0.5 }}>
          Recent POW appointments
        </Typography>
      </Box>
      {loading ? (
        <div style={{ marginTop: '40px' }}>
          <CardLoader />
        </div>
      ) : POWEncounters.length > 0 ? (
        <>
          <Typography className={classes.lightText}>
            Cross-reference previous POW appointments with AdvancedMD encounter
            history to confirm profile link.
          </Typography>
          <Box
            style={{
              overflow: 'auto',
              height: 'calc(100vh - 430px)',
              paddingRight: '5px'
            }}
          >
            {POWEncounters.map((encounter, index) => {
              return (
                <AMDLinkCard key={index} sx={{ mt: 2, p: 2 }} elevation={0}>
                  <Typography className={classes.lightText}>
                    {convertToStoreLocalTime(
                      encounter?.timestamp,
                      'MM/DD/YYYY'
                    )}
                  </Typography>
                  <Typography className={classes.lightText} sx={{ mb: 1 }}>
                    {convertToStoreLocalTime(encounter?.timestamp, 'h:mm A')}
                  </Typography>
                  <Typography className={classes.strongText} sx={{ mb: 1 }}>
                    {capitalize(encounter?.type)}
                  </Typography>
                  {encounter?.purchasableItems &&
                    encounter?.purchasableItems.map((item, i) => (
                      <Typography key={i} className={classes.text}>
                        {item?.description}
                      </Typography>
                    ))}
                </AMDLinkCard>
              );
            })}
          </Box>
        </>
      ) : (
        <Typography className={classes.lightText}>
          No POW appointments found.
        </Typography>
      )}
    </Box>
  );
};

export default POWClientDetails;
